import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';

interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

const Faq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const faqItems: FaqItem[] = [
    {
      question: 'Ce presupune adopția unui câine?',
      answer: 'Adopția unui câine implică completarea unui formular, vizita la adăpost pentru a cunoaște animalul, verificarea condițiilor de adopție, semnarea contractului și achitarea taxei de adopție de 100 RON. După adopție, vei primi toate actele câinelui, inclusiv carnetul de sănătate și certificatul de microcipare.',
      category: 'adoption',
    },
    {
      question: 'Ce documente îmi trebuie pentru a adopta un câine?',
      answer: 'Pentru a adopta un câine ai nevoie de cartea de identitate și să completezi formularul nostru de adopție. De asemenea, va trebui să semnezi contractul de adopție care stabilește responsabilitățile tale ca proprietar.',
      category: 'adoption',
    },
    {
      question: 'Câinii sunt vaccinați și deparazitați?',
      answer: 'Da, toți câinii din adăpostul nostru sunt vaccinați conform vârstei, deparazitați intern și extern, și microcipați. La momentul adopției, câinele va avea carnetul de sănătate cu toate informațiile actualizate.',
      category: 'health',
    },
    {
      question: 'Ce fac dacă animalul adoptat se îmbolnăvește?',
      answer: 'În cazul în care animalul adoptat prezintă probleme de sănătate în primele 14 zile de la adopție, te rugăm să ne contactezi. Avem parteneriate cu clinici veterinare care pot oferi consultații la prețuri reduse pentru animalele adoptate de la noi.',
      category: 'health',
    },
    {
      question: 'Există o perioadă de probă pentru adopție?',
      answer: 'Da, oferim o perioadă de acomodare de 14 zile, în care poți decide dacă tu și animalul vă potriviți. În cazul în care apar probleme majore de adaptare, câinele poate fi returnat la adăpost fără penalități. Adopțiile sunt gratuite la acest adăpost.',
      category: 'adoption',
    },
    {
      question: 'Pot adopta un câine dacă locuiesc la apartament?',
      answer: 'Da, mulți dintre câinii noștri se adaptează perfect la viața la apartament. În profilul fiecărui câine menționăm dacă este potrivit pentru apartament sau are nevoie de o curte. Cel mai important este să îi oferi suficiente plimbări și activitate fizică.',
      category: 'adoption',
    },
    {
      question: 'Cum pot face o donație pentru adăpost?',
      answer: 'Poți face donații în mai multe moduri: prin transfer bancar, prin PayPal, sau poți dona direct la adăpost. De asemenea, acceptăm și donații în natură precum hrană, paturi, jucării, medicamente etc. Toate detaliile se găsesc pe pagina de donații.',
      category: 'donation',
    },
    {
      question: 'Cum sunt folosite donațiile?',
      answer: 'Donațiile sunt folosite pentru îngrijirea animalelor: hrană, tratamente medicale, vaccinuri, deparazitări, microcipare, dar și pentru întreținerea adăpostului și acoperirea cheltuielilor de utilități. Publicăm rapoarte periodice despre modul în care sunt folosite donațiile.',
      category: 'donation',
    },
    {
      question: 'Pot deveni voluntar la adăpost?',
      answer: 'Da, suntem mereu în căutare de voluntari pasionați! Poți ajuta la plimbarea câinilor, curățenie, socializare, organizarea de evenimente, administrarea rețelelor sociale sau strângerea de fonduri. Completează formularul nostru de voluntariat pentru a afla mai multe despre programul nostru.',
      category: 'volunteer',
    },
    {
      question: 'Organizați evenimente de adopție?',
      answer: 'Da, organizăm periodic evenimente de adopție în diferite locații din oraș, unde puteți cunoaște câinii disponibili pentru adopție. Anunțăm aceste evenimente pe pagina noastră de Facebook și pe site-ul nostru.',
      category: 'events',
    },
    {
      question: 'Ce se întâmplă cu câinii care nu sunt adoptați?',
      answer: 'Câinii rămân în grija noastră până când găsesc o familie potrivită. Nu practicăm eutanasierea animalelor sănătoase, indiferent de timpul petrecut în adăpost. Încercăm să le oferim cele mai bune condiții și să îi promovăm activ pentru adopție.',
      category: 'adoption',
    },
    {
      question: 'Pot returna un câine dacă adopția nu funcționează?',
      answer: 'Da, deși sperăm ca fiecare adopție să fie definitivă, înțelegem că pot apărea situații neprevăzute. În contractul de adopție există o clauză care permite returnarea animalului la adăpost în cazul în care nu vă puteți adapta reciproc.',
      category: 'adoption',
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filterFaqItems = () => {
    let filteredItems = faqItems;

    if (activeCategory !== 'all') {
      filteredItems = filteredItems.filter(item => item.category === activeCategory);
    }

    if (searchTerm) {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      filteredItems = filteredItems.filter(
        item =>
          item.question.toLowerCase().includes(lowercaseSearchTerm) ||
          item.answer.toLowerCase().includes(lowercaseSearchTerm)
      );
    }

    return filteredItems;
  };

  const categories = [
    { id: 'all', name: 'Toate' },
    { id: 'adoption', name: 'Adopție' },
    { id: 'health', name: 'Sănătate' },
    { id: 'donation', name: 'Donații' },
    { id: 'volunteer', name: 'Voluntariat' },
    { id: 'events', name: 'Evenimente' },
  ];

  const filteredFaqItems = filterFaqItems();

  return (
    <>
      <SEOHead 
        title="Întrebări Frecvente - Adopție Câini București"
        description="Răspunsuri la întrebări frecvente despre adopția câinilor la Adăpostul Odăi, București. Află despre procesul de adopție, documente necesare, îngrijirea câinilor și multe altele."
        keywords="intrebari frecvente adopție caini, faq adopta caini bucuresti, proces adopție caini, documente adopție câini, îngrijire câini adoptați, adaspost odai faq"
        url="/intrebari"
        type="website"
        image="https://images.pexels.com/photos/3361739/pexels-photo-3361739.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      <PageHeader
        title="Întrebări frecvente"
        subtitle="Aici găsești răspunsuri la cele mai comune întrebări despre adopție, donații și îngrijirea animalelor"
        image="https://images.pexels.com/photos/3361739/pexels-photo-3361739.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Search */}
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Caută întrebări..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <Search
                size={20}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mb-8 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFaqItems.length > 0 ? (
              filteredFaqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left p-4 bg-white hover:bg-gray-50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-primary">{item.question}</span>
                    {openIndex === index ? (
                      <ChevronUp size={20} className="text-primary" />
                    ) : (
                      <ChevronDown size={20} className="text-primary" />
                    )}
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700">{item.answer}</p>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  Nu am găsit întrebări care să corespundă căutării tale. Încearcă alți termeni sau contactează-ne direct!
                </p>
              </div>
            )}
          </div>

          {/* Still have questions */}
          <div className="mt-12 p-6 bg-primary-100 rounded-lg text-center">
            <h3 className="text-xl font-bold text-primary mb-3">Ai întrebări suplimentare?</h3>
            <p className="text-gray-700 mb-4">
              Nu ai găsit răspunsul la întrebarea ta? Contactează-ne și îți vom răspunde cât mai curând posibil.
            </p>
            <a
              href="/contact"
              className="inline-block px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            >
              Contactează-ne
            </a>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Faq;